<template>
	<Modal
		ref="paywallModal"
		modal-name="PaywallModal"
		type="dialog"
		class="paywall__modal"
		:theme="$arenaConfig.isLightTheme ? null : 'dark'"
		:closeable="true"
		data-testid="paywall-modal"
	>
		<template #title>
			<span data-testid="paywall-title">{{ isGeoblocked ? $I18n.trans('event.geoblocked.title') : $I18n.trans('payments.paywall.title') }}</span>
		</template>
		<template #content>
			<template v-if="isGeoblocked">
				<V2Icon
					class="geo-blocking__icon"
					icon-name="world-pin"
				/>
				<div
					class="geo-blocking__message"
					data-testid="geoblocking-message"
					v-html="geoblockingMessage"
				/>
			</template>
			<template v-else>
				<div
					v-if="showPaywallDescription"
					class="paywall__desc"
				>
					{{ $I18n.trans(`payments.paywall.${isLoggedIn ? 'content_logged_in' : 'content_logged_out'}`) }}
				</div>
				<template v-if="!isExternalPayment">
					<div
						v-if="groupedOffers && groupedOffers.length > 0"
						class="paywall__grouped-offers"
					>
						<div
							v-for="groupedOffer in groupedOffers"
							:key="groupedOffer.type"
							class="paywall__grouped-offer"
						>
							<h3 class="paywall__grouped-offer-title">
								{{ groupedOffer.title }}
							</h3>
							<p class="paywall__grouped-offer-price">
								{{ $I18n.trans('payments.price_from') }} {{ formatCurrency(groupedOffer.currencyCode, groupedOffer.minPrice) }}
							</p>
						</div>
					</div>
					<template v-else-if="paymentOffers && paymentOffers.length > 0">
						<div
							v-for="offer in paymentOffers"
							:key="offer.id"
							class="paywall__offer"
						>
							{{ $Helpers.getTranslatedContent(offer.name) }}<span v-if="!offer.isExternal">: {{ formatCurrency(offer.currencyCode, offer.amount) }}</span>
							<span
								v-if="offer.type === PaymentOfferType.SUBSCRIPTION"
								class="paywall__recurring"
							>
								/ {{ $I18n.trans('payments.validity.' + offer.validity) }}
							</span>
							<div
								v-if="offer.voucherEnabled"
								class="payment__offer-desc"
							>
								{{ $I18n.trans('payments.paywall.use_voucher') }}
							</div>
						</div>
					</template>
					<div
						v-else-if="paymentOffers"
						class="paywall__offer"
					>
						{{ $I18n.trans('payments.paywall.no_offers_event') }}
					</div>
				</template>
			</template>
		</template>
		<template #footer>
			<div class="l-button-group">
				<Button
					v-if="isLoggedIn && computedBackUrl && !$Helpers.isSmartTV()"
					:text="$Helpers.ucfirst($I18n.trans('commons.back'))"
					theme="dark"
					modifier="secondary"
					@click="back()"
				/>
				<Button
					v-if="canContinue && !isLoggedIn"
					class="paywall__login"
					:text="$I18n.trans('arena.login.error_page.button')"
					theme="dark"
					modifier="secondary"
					data-testid="paywall-login"
					@click="openLoginModal()"
				/>
				<Button
					v-if="canContinue"
					class="paywall__purchase"
					:disabled="cannotPurchase"
					:text="paywallButtonLabel"
					:link="paywallButtonLink || null"
					:is-external-link="!!paywallButtonLink"
					:busy="busy"
					theme="dark"
					autofocus
					data-key="enter"
					data-testid="paywall-purchase"
					@click="handlePurchaseClick()"
				/>
			</div>
		</template>
	</Modal>
</template>

<script>
import { formatCurrency } from '@shared/utils/formatNumber';
import { mapGetters, mapState } from 'vuex';
import Modal from '@shared/components/Modal';
import Button from '@shared/components/Button';
import V2Icon from '@shared/components/Icon';
import { PaymentOfferType } from '@shared/enums/PaymentOfferType';
import { getGroupedOffers } from '@js/components/Payments/getGroupedOffers';
import { EventAccessRestriction } from '@shared/enums/EventAccessRestriction';
import { httpClient } from '@shared/js/api/httpClient';

export default {
	name:       'PaywallModal',
	components: { Modal, Button, V2Icon },
	props:      {
		backUrl: {
			type:     String,
			required: false,
			default:  null,
		},
		event: {
			type:     Object,
			required: true,
		},
	},
	data() {
		return {
			busy:              false,
			paymentOffers:     null,
			action:            window.location.hash.replace('#', '').split('/')[0],
			rawAction:         window.location.hash.replace(/^#/, ''),
			qrCode:            window.location.hash.split('/qr/').length > 1 ? window.location.hash.split('/qr/')[1] : null,
			isGeoblocked:      false,
			initializingModal: false,
			PaymentOfferType,
		};
	},
	computed: {
		...mapState({
			profileIsInitialized: state => state.user.isInitialized,
		}),
		...mapGetters({
			hasAccessToEvent: 'user/hasAccessToEvent',
			purchasedEvents:  'user/purchasedEvents',
			subscriptions:    'user/subscriptions',
			isLoggedIn:       'user/isLoggedIn',
			context:          'user/context',
			isFanAccount:     'user/isFanAccount',
			isManagerAccount: 'user/isManagerAccount',
			profile:          'user/profile',
		}),
		isExternalPayment() {
			return this.$arenaConfig.payment?.isExternal ?? false;
		},
		paywallButtonLink() {
			const link = this.$arenaConfig.payment?.paywallButtonLink;
			return typeof link === 'string' && link.trim() !== '' ? link.trim() : null;
		},
		paywallButtonLabel() {
			const customLabel = this.$Helpers.getTranslatedContent(this.$arenaConfig.payment?.paywallButtonText);
			return customLabel || this.$I18n.trans('payments.paywall.button_purchase');
		},
		showPaywallDescription() {
			return this.isExternalPayment || (this.paymentOffers && this.paymentOffers.length > 0);
		},
		cannotPurchase() {
			if (!this.paymentOffers || this.paymentOffers.length === 0) {
				return true;
			}

			if (this.isExternalPayment) {
				return !this.paywallButtonLink;
			}

			return false;
		},
		computedBackUrl() {
			return this.backUrl || $Helpers.getBackUrl();
		},
		geoblockingMessage() {
			return window.$Helpers.getTranslatedContent(window.arenaConfig.geoblocking?.message) || $I18n.trans('event.geoblocked.message');
		},
		canContinue() {
			return !this.isGeoblocked;
		},
		hasPreview() {
			return (this.paymentOffers || []).some(obj => obj.previewSeconds > 0);
		},
		isUpcoming() {
			return this.event && this.event.liveInSeconds > 0;
		},
		groupedOffers() {
			if (!this.paymentOffers || this.paymentOffers.length === 0 || !this.$arenaConfig.paymentOfferGroups) return [];
			const groups = this.$arenaConfig.paymentOfferGroups || [];
			return getGroupedOffers(this.paymentOffers, groups);
		},
	},
	watch: {
		profileIsInitialized() {
			if (!this.hasAccessToEvent(this.event) && !this.isManagerAccount) {
				this.initializeModal();
			}
		},
	},
	mounted() {
		window.addEventListener('keydown', this.handleKeyDown);
		// Age confirmation and other conditional views can mount the paywall after profile loading finished.
		if (this.profileIsInitialized && !this.hasAccessToEvent(this.event) && !this.isManagerAccount) {
			this.initializeModal();
		}
	},
	beforeDestroy() {
		window.removeEventListener('keydown', this.handleKeyDown);
	},
	methods: {
		async initializeModal() {
			if (this.initializingModal) {
				return;
			}

			this.initializingModal = true;
			try {
				await this.getPaymentOffers();
				const isInPaymentFlow = ['login', 'checkout', 'confirm'].includes(this.action);
				const showPaywall = this.action === 'buy' || !this.hasPreview || this.isUpcoming;

				// Returning from login (or refresh mid-flow) appends #checkout — resume payment instead of re-showing the paywall.
				if (isInPaymentFlow && !this.isExternalPayment) {
					const data = {
						action: this.action === 'confirm' ? 'confirm' : (this.rawAction || 'checkout'),
					};

					if (this.qrCode) {
						data.qrCode = this.qrCode;
					}
					this.eventBus.$emit('modal:PaymentModal', data);
					return;
				}

				if (this.isExternalPayment || showPaywall) {
					this.eventBus.$emit('modal:PaywallModal');
				}
			} finally {
				this.initializingModal = false;
			}
		},
		openLoginModal() {
			if (window.isMz) {
				this.eventBus.$emit('modal:MzLoginModal');
				return;
			}

			if ($Helpers.isSmartTV()) {
				this.eventBus.$emit('modal:QrLoginModal', {
					redirectAfterLoginUrl: window.location.href,
					eventId:               this.event.id,
				});
				return;
			}

			this.eventBus.$emit('modal:LoginModal');
		},
		handlePurchaseClick() {
			if (this.paywallButtonLink) {
				return;
			}

			this.openPurchaseModal();
		},
		openPurchaseModal() {
			const data = { action: 'checkout' };

			if (this.qrCode) {
				data.qrCode = this.qrCode;
			}

			this.eventBus.$emit('modal:PaymentModal', data);
		},
		back() {
			window.location.href = this.computedBackUrl;
		},
		async getPaymentOffers() {
			try {
				const response = await httpClient.getPaymentOffers({ eventId: this.event.id });
				const offers = Array.isArray(response.data) ? response.data : [];
				const visibleOffers = offers.filter(offer => !offer.geoblocked);
				this.paymentOffers = visibleOffers;
				this.isGeoblocked = offers.length > 0 && visibleOffers.length === 0;
			} catch (error) {
				this.paymentOffers = [];
				if (error.response?.data?.reason === EventAccessRestriction.GEOBLOCKED) {
					this.isGeoblocked = true;
				}
			}
		},
		formatCurrency,
		handleKeyDown(event) {
			const modalIsOpen = this.$refs.paywallModal && this.$refs.paywallModal.modalActive;
			if (!event.key || !modalIsOpen) {
				return;
			}
			const key = event.key.toUpperCase();
			// handle ENTER on SmartTV (also fine for non SmartTV)
			if (key === 'ENTER' && !this.cannotPurchase && !this.paywallButtonLink) {
				this.openPurchaseModal();
				window.removeEventListener('keydown', this.handleKeyDown);
			}
		},
	},

};
</script>

<style lang="scss" scoped>
@import '@sass/variables';
$context-col-width: 85px;

.paywall__offer {
	padding: 24px;
	background-color: var(--color-grey-950);
	margin-bottom: $sp3;
}

.paywall__desc + .paywall__grouped-offers,
.paywall__desc + .paywall__offer {
	margin-top: $sp6;
}

.payment__offer-desc {
	margin-top: 6px;
	color: var(--color-grey-300);
}

.geo-blocking__icon.v2-icon.-size-md {
	margin-bottom: $sp6;
	width: 64px;
	height: 64px;
}

.geo-blocking__message::v-deep a { // stylelint-disable-line
	text-decoration: underline;
}

.paywall__recurring {
	color: var(--color-grey-300);
}

.paywall__grouped-offers {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: $sp4;
	overflow-x: auto;
}

.paywall__grouped-offer {
	width: 100%;
	padding: $sp4;
	background-color: var(--color-grey-950);
	border-radius: $border-radius--lg;
}

.paywall__grouped-offer-title {
	@include font(primary, medium, regular, fs-80);
	margin-bottom: $sp2;
}

.paywall__grouped-offer-price {
	@include font-size(fs-80);
	color: var(--color-grey-300);
}
</style>
